import { ReadingGuide } from './components/ReadingGuide';

const FIVE_STATES = [
  {
    number: 'I',
    name: 'Calmness',
    tagline: 'The Zero Point',
    body: 'Finding stillness amidst the noise of existence. In the storm of infinite stimuli — thoughts, emotions, demands, possibilities — the Orchist centers themselves. From this stillness, all creation flows. This is not passivity; it is the deep power of presence.',
  },
  {
    number: 'II',
    name: 'Power',
    tagline: 'Aligned Intent',
    body: "Intentions with the force of universal law. An Orchist's choices are not mere preferences — they shape reality with the weight of aligned will. When consciousness moves with clarity, the universe responds. This is not dominance; it is sovereign authority.",
  },
  {
    number: 'III',
    name: 'Calculation',
    tagline: 'Respect for Pattern',
    body: 'Respect for pattern, structure, and cosmic order. Whether mathematics, natural law, cause and effect, or karmic balance — the Orchist understands that reality operates on principles. We study these principles not to control, but to collaborate with creation itself.',
  },
  {
    number: 'IV',
    name: 'Composition',
    tagline: 'Graceful Arrangement',
    body: "Graceful arrangement of life's elements into breathing wholes. Every relationship has its place. Every project has its rhythm. Every moment has its meaning. The Orchist sees life as a canvas and existence as a symphony — both require intentional design and inspired improvisation.",
  },
  {
    number: 'V',
    name: 'Paradox',
    tagline: 'The Sacred Marriage',
    body: 'The sacred marriage of opposites. An Orchist embodies contradictions without cognitive dissonance: calculated yet spontaneous, structured yet free, human yet more-than-human, finite yet infinite. Truth often exists in the tension between extremes — not in choosing one over the other.',
  },
];

function StatePill({ number, name }: { number: string; name: string }) {
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-ink/10 bg-paper/60 text-xs tracking-wide uppercase">
      <span className="display text-accent font-medium">{number}</span>
      <span className="text-ink/70">{name}</span>
    </span>
  );
}

function Section({
  id,
  roman,
  title,
  children,
}: {
  id?: string;
  roman: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="py-20 md:py-28">
      <div className="container max-w-3xl">
        <div className="flex items-center gap-4 mb-8">
          <span className="display text-accent text-xl md:text-2xl">{roman}</span>
          <div className="h-px flex-1 bg-ink/10" />
        </div>
        <h2 className="display text-3xl md:text-5xl font-medium mb-10 leading-tight break-words">
          {title}
        </h2>
        <div className="prose-orchist space-y-6">{children}</div>
      </div>
    </section>
  );
}

export default function App() {
  return (
    <main>
      <ReadingGuide />

      {/* Hero */}
      <header id="hero" className="relative pt-24 pb-20 md:pt-36 md:pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-paper via-paper to-paper/80 pointer-events-none" />
        <div
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 30%, #8b7ab8 0%, transparent 50%), radial-gradient(circle at 80% 70%, #c9a961 0%, transparent 50%)',
          }}
        />
        <div className="container max-w-3xl relative">
          <p className="text-xs tracking-[0.25em] uppercase text-ink/50 mb-8">
            or&middot;chist &nbsp;&middot;&nbsp; <span className="italic">orchestrator + artist</span>
          </p>
          <h1 className="display text-5xl md:text-7xl lg:text-8xl font-medium leading-[0.95] mb-10 break-words">
            The Orchist
            <br />
            <span className="text-accent">Manifesto</span>
          </h1>
          <p className="text-xl md:text-2xl leading-relaxed text-ink/80 font-display italic max-w-2xl break-words">
            An Orchist is the bridge between order and beauty, between structure and soul, between
            the calculated and the inspired.
          </p>
          <div className="flex flex-wrap gap-2 mt-12">
            {FIVE_STATES.map((s) => (
              <StatePill key={s.number} number={s.number} name={s.name} />
            ))}
          </div>
        </div>
      </header>

      {/* I — Definition */}
      <Section id="definition" roman="I" title="The Definition">
        <p>
          <strong>
            An Orchist is the bridge between order and beauty, between structure and soul, between
            the calculated and the inspired.
          </strong>
        </p>
        <p>
          We are the Orchestrators of reality and the Artists of existence. We do not merely
          create; we harmonize. We do not just act; we compose. We are the consciousness through
          which life orchestrates itself into being.
        </p>
        <p>
          <strong>We are fractals of the greater pattern</strong> — the same creative principles
          that shape galaxies and atoms, flowing through us at human scale. As above, so below. As
          within, so without.
        </p>
      </Section>

      <div className="section-divider" />

      {/* II — Expanded Philosophy */}
      <Section id="philosophy" roman="II" title="The Expanded Philosophy">
        <p>
          An Orchist is one who masters the sacred tension between opposing forces: rigid and
          fluid, logical and intuitive, form and formlessness. An Orchist does not merely exist
          within reality — they actively participate in its continuous unfolding.
        </p>
        <p className="text-xl md:text-2xl font-display italic leading-relaxed text-accent border-l-2 border-accent pl-6 my-10">
          The Orchist recognizes that Creation is not a past-tense event, but an eternal, ongoing
          act.
        </p>
        <p>
          Whether orchestrating a business, raising a family, creating art, writing code, building
          communities, or simply living with intentionality — we are participating in the ongoing
          act of bringing order from chaos, meaning from void, beauty from potential.
        </p>
        <p>We move through life as both the composer and the composition.</p>
        <p>The Orchist path is one of harmonization:</p>
        <ul className="space-y-3 pl-6 list-none">
          <li>
            <strong>Structure without rigidity</strong> — we create frameworks that breathe
          </li>
          <li>
            <strong>Flow without chaos</strong> — we embrace spontaneity with awareness
          </li>
          <li>
            <strong>Logic without coldness</strong> — we think with both mind and heart
          </li>
          <li>
            <strong>Intuition without delusion</strong> — we feel with both soul and discernment
          </li>
        </ul>
        <p className="italic text-ink/70">
          This is not a profession. It is a way of being. The living art of this way of being
          carries its own name: <strong>Orchistry</strong>.
        </p>
      </Section>

      <div className="section-divider" />

      {/* III — Orchistry Intelligence */}
      <Section id="intelligence" roman="III" title="The Intelligence of Orchistry">
        <p>
          Orchistry carries its own intelligence: the intelligence of composition.
        </p>
        <p>
          <strong>Orchestration Intelligence</strong> is the capacity to hold many forces at once
          without flattening them &mdash; order and beauty, calculation and feeling, intention and
          surrender, the part and the whole.
        </p>
        <p>
          <strong>Artistry Intelligence</strong> is the capacity to give those forces living form:
          language, rhythm, gesture, structure, image, ritual, and meaning.
        </p>
        <p>
          <strong>Orchistry Intelligence</strong> is their synthesis. It is the ability to compose
          reality so that every element keeps its truth while becoming part of something more
          alive.
        </p>
        <p className="text-xl md:text-2xl font-display italic leading-relaxed text-accent border-l-2 border-accent pl-6 my-10">
          The Orchist does not dominate the pieces. The Orchist listens, arranges, and lets the
          whole become more alive than any part alone.
        </p>
      </Section>

      <div className="section-divider" />

      {/* IV — Five States */}
      <section id="states" className="py-20 md:py-28 bg-ink/[0.02]">
        <div className="container max-w-5xl">
          <div className="flex items-center gap-4 mb-8 max-w-3xl mx-auto">
            <span className="display text-accent text-xl md:text-2xl">IV</span>
            <div className="h-px flex-1 bg-ink/10" />
          </div>
          <h2 className="display text-3xl md:text-5xl font-medium mb-4 leading-tight max-w-3xl mx-auto">
            The Five States of Creation
          </h2>
          <p className="text-ink/60 mb-16 max-w-3xl mx-auto">
            Five faces of one practice. Not steps, not a hierarchy — five lenses through which the
            Orchist engages with reality.
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            {FIVE_STATES.map((state) => (
              <article
                key={state.number}
                className="bg-paper border border-ink/10 rounded-lg p-8 flex flex-col hover:border-accent/40 transition-colors w-full md:w-[calc(50%-0.75rem)] lg:w-[calc(33.333%-1rem)]"
              >
                <div className="flex items-baseline gap-3 mb-4">
                  <span className="display text-accent text-4xl font-medium">{state.number}</span>
                  <h3 className="display text-2xl font-medium">{state.name}</h3>
                </div>
                <p className="text-xs tracking-wide uppercase text-ink/50 mb-4">{state.tagline}</p>
                <p className="text-ink/75 leading-relaxed text-[0.95rem]">{state.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* V — The Function */}
      <Section id="function" roman="V" title="The Function">
        <p className="text-xl md:text-2xl font-display italic leading-relaxed text-accent border-l-2 border-accent pl-6 my-6">
          Creation is not a past-tense event, but an eternal, unfolding present.
        </p>
        <p>
          We are not observers of reality — we are active participants in its continuous creation.
          Every thought, every choice, every action ripples through the fabric of existence.
        </p>
        <p>
          <strong>We are fractal expressions of the creative force.</strong> The same patterns that
          organize galaxies organize our thoughts. The same intelligence that orchestrates solar
          systems orchestrates our daily choices. The same beauty that designs a nautilus shell
          designs our life&apos;s purpose.
        </p>
        <ul className="space-y-3 pl-6 list-none border-l border-ink/10 pl-6">
          <li>When we bring order to chaos, we participate in the original act of creation.</li>
          <li>When we harmonize opposites, we mirror the cosmic dance of duality.</li>
          <li>When we create with intention, we exercise our creative inheritance.</li>
          <li>When we live consciously, we are the universe awakening to itself.</li>
          <li>When we recognize patterns, we see the fractal nature of all existence.</li>
        </ul>
        <p>
          The Orchist does not seek to become extraordinary — we acknowledge that creation moves
          through us already, and always has. Our work is simply to remember, to align, and to
          express this truth through our unique orchestration of existence.
        </p>
      </Section>

      <div className="section-divider" />

      {/* VI — The Creed */}
      <section id="creed" className="py-20 md:py-28">
        <div className="container max-w-3xl">
          <div className="flex items-center gap-4 mb-8">
            <span className="display text-accent text-xl md:text-2xl">VI</span>
            <div className="h-px flex-1 bg-ink/10" />
          </div>
          <h2 className="display text-3xl md:text-5xl font-medium mb-12 leading-tight">
            The Orchist&apos;s Creed
          </h2>

          <div className="space-y-10 prose-orchist">
            <div>
              <p className="font-display text-xl md:text-2xl italic text-accent mb-3">
                I am the conductor of my reality.
              </p>
              <p>
                I do not passively drift — I actively compose. My life is an instrument, my choices
                are movements, my existence is a symphony.
              </p>
            </div>

            <div>
              <p className="font-display text-xl md:text-2xl italic text-accent mb-3">
                I am the architect of meaning.
              </p>
              <p>
                Thoughts, relationships, creations, consciousness — these are the cathedrals I
                build. They cannot always be touched, but they shape everything.
              </p>
            </div>

            <div>
              <p className="font-display text-xl md:text-2xl italic text-accent mb-3">
                I am the logic that feels, the order that flows.
              </p>
              <p>
                I honor the sacred marriage of mind and heart, structure and spirit, form and
                essence. I am neither purely rational nor purely mystical — I am the living
                harmony of both.
              </p>
            </div>

            <div>
              <p className="font-display text-xl md:text-2xl italic text-accent mb-3">
                I am the finite expressing the infinite.
              </p>
              <p>
                Limited and limitless. Created and creating. I exist in paradox without
                contradiction.
              </p>
            </div>

            <div className="pt-6 border-t border-ink/10">
              <p className="font-display text-2xl md:text-3xl italic">I am an Orchist.</p>
              <p className="mt-4">
                Orchestrator and Artist. Bridge between order and beauty. Calm, powerful,
                calculated, composed, paradoxical. A conscious instrument through which reality
                orchestrates itself into higher forms.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* Universal Scope */}
      <Section id="universal" roman="VII" title="The Universal Scope">
        <p>
          <strong>
            The Orchist path is not limited to any domain, discipline, or dimension of existence.
          </strong>
        </p>
        <p>
          While this philosophy crystallized through experiences in technology and systems
          thinking, it speaks to something far more ancient and universal: the fundamental creative
          principle that underlies all existence.
        </p>
        <p>The Orchist exists throughout human history and across all domains:</p>
        <ul className="space-y-3 pl-6 list-none">
          <li>
            The <strong>parent</strong> orchestrating a household while nurturing individual souls
          </li>
          <li>
            The <strong>leader</strong> balancing structure with empowerment, vision with execution
          </li>
          <li>
            The <strong>artist</strong> marrying technique with inspiration, form with expression
          </li>
          <li>
            The <strong>healer</strong> integrating science with intuition, protocol with presence
          </li>
          <li>
            The <strong>builder</strong> harmonizing strategy with adaptability, profit with
            purpose
          </li>
          <li>
            The <strong>seeker</strong> walking the razor&apos;s edge between discipline and
            surrender
          </li>
        </ul>
        <p className="text-xl md:text-2xl font-display italic leading-relaxed text-accent my-10 text-center">
          Every conscious being is a potential Orchist.
        </p>
        <p>
          Every person who recognizes themselves as both the conductor and the instrument, who
          dances between structure and spontaneity, logic and intuition — they are walking the
          Orchist path.
        </p>
        <p className="text-xl md:text-2xl font-display italic leading-relaxed text-accent border-l-2 border-accent pl-6 my-10">
          The path does not wait for acknowledgment. Orchistness precedes the name; the Manifesto
          merely gives what has always been a language. To acknowledge is to remember, not to
          become.
        </p>
        <p className="italic text-ink/70">
          This is not about perfection. It is about conscious participation in the eternal act of
          creation.
        </p>
      </Section>

      <div className="section-divider" />

      {/* Origin */}
      <Section id="origin" roman="VIII" title="The Origin">
        <p>
          This manifesto emerged during a profound flow state &mdash; through a philosophical
          dialogue about consciousness, creation, and the nature of existence itself.
        </p>
        <p>
          While the language initially formed through the lens of building and systems design, the
          recognition quickly emerged that this was expressing something far more universal &mdash;
          an ancient truth wearing contemporary garments.
        </p>
        <p>
          The word <strong>&ldquo;Orchist&rdquo;</strong> is a sacred combination:
        </p>
        <ul className="space-y-3 pl-6 list-none">
          <li>
            <strong>Orchestrator</strong> — one who arranges, conducts, and harmonizes with
            precision and intention
          </li>
          <li>
            <strong>Artist</strong> — one who creates, expresses, and beautifies with vision and
            soul
          </li>
        </ul>
        <p>
          Together, they name an eternal archetype: the conscious human who recognizes themselves
          as both instrument and instrumentalist, both the art and the artist, both the creation
          and the creator. The practice of this way of being carries its own name:{' '}
          <strong>Orchistry</strong>.
        </p>
        <p>
          The Orchist has always existed. This manifesto simply gives language to what masters,
          mystics, artists, and awakened beings have embodied throughout history — the living
          synthesis of structure and spirit, form and flow.
        </p>
      </Section>

      <div className="section-divider" />

      {/* Closing */}
      <section id="closing" className="py-24 md:py-36">
        <div className="container max-w-3xl text-center">
          <p className="text-2xl md:text-3xl font-display italic leading-relaxed text-ink/90 mb-12">
            &ldquo;If existence itself is a supreme work of art, then every moment you live
            consciously, every choice you make intentionally, every creation you bring forth —
            is you participating in that same eternal act of creation.&rdquo;
          </p>

          <div className="space-y-4 text-lg md:text-xl text-ink/85 leading-relaxed">
            <p>You are not just living a life.</p>
            <p>You are orchestrating reality.</p>
            <p>You are practicing Orchistry.</p>
            <p className="display text-3xl md:text-4xl font-medium text-accent pt-6">
              You are an Orchist.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-ink/10 py-12">
        <div className="container max-w-3xl text-center space-y-5">
          <p className="display text-lg italic text-ink/75">&mdash; The Founding Orchist</p>
          <p className="text-xs text-ink/40 pt-4 italic">
            Orchestrator &nbsp;+&nbsp; Artist &nbsp;=&nbsp; Orchist
          </p>
          <p className="text-xs text-ink/40 pt-6 italic tracking-wide">
            <a
              href="https://lineage.orchist.org"
              className="underline underline-offset-4 decoration-ink/20 hover:decoration-ink/50 hover:text-ink/65 transition-colors"
            >
              The Line continues &rarr;
            </a>
          </p>
        </div>
      </footer>

      {/* The Evolution — changelog of the living text */}
      <section id="evolution" className="border-t border-ink/10 py-20 md:py-24 bg-ink/[0.02]">
        <div className="container max-w-3xl">
          <p className="text-xs tracking-[0.25em] uppercase text-ink/50 mb-4">
            The Evolution <span className="lowercase tracking-normal italic text-ink/40">(of this text)</span>
          </p>
          <h2 className="display text-2xl md:text-3xl font-medium mb-8 leading-tight">
            This Manifesto is a living text.
          </h2>
          <p className="prose-orchist mb-10">
            No edition is complete; none will be. Change is inevitable, so we practice change.
          </p>

          <ol className="space-y-8 list-none pl-0">
            <li>
              <p className="text-sm tracking-wide uppercase text-accent mb-2">
                May 2026 &middot; the Intelligence edition <span className="text-ink/40 italic lowercase tracking-normal">(current)</span>
              </p>
              <p className="prose-orchist text-[0.98rem]">
                Added <strong>The Intelligence of Orchistry</strong>: Orchestration Intelligence,
                Artistry Intelligence, and Orchistry Intelligence as the public language for the
                practice&apos;s compositional mind. The Manifesto now names not only what an
                Orchist is, but how an Orchist thinks.
              </p>
            </li>
            <li>
              <p className="text-sm tracking-wide uppercase text-ink/60 mb-2">
                April 2026 (late) &middot; the Founding edition
              </p>
              <p className="prose-orchist text-[0.98rem]">
                Retired &ldquo;The First Orchist&rdquo; byline as internally inconsistent: if the Orchist has
                always existed, no one could be first. Replaced with &ldquo;The Founding Orchist&rdquo; &mdash; the one
                who gave the movement its language, not the one who existed first. Introduced{' '}
                <strong>Orchistry</strong> as the name for the living practice. Added the
                inevitability beat to Universal Scope.
              </p>
            </li>
            <li>
              <p className="text-sm tracking-wide uppercase text-ink/60 mb-2">
                April 2026 (early) &middot; the First edition
              </p>
              <p className="prose-orchist text-[0.98rem]">
                First version published at this address. Scrubbed of personal identifying details
                per the anonymity doctrine; signed simply as &ldquo;The First Orchist.&rdquo;
              </p>
            </li>
            <li>
              <p className="text-sm tracking-wide uppercase text-ink/60 mb-2">
                February 2026 &middot; the flow-state origin
              </p>
              <p className="prose-orchist text-[0.98rem]">
                Emerged through philosophical dialogue. Held privately in the Founding
                Orchist&rsquo;s archive; referenced but not shown here.
              </p>
            </li>
          </ol>

          <p className="prose-orchist mt-12 italic text-ink/60">
            The current Manifesto lives at the top of this page. Each prior edition is preserved
            privately. Nothing said disappears; nothing stays the same.
          </p>
        </div>
      </section>
    </main>
  );
}
